import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

const InvoicePreview = ({ orderId, token, onClose }) => {
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [layout, setLayout] = useState('80mm'); // '58mm', '80mm', 'A4'

    useEffect(() => {
        const fetchOrderDetails = async () => {
            try {
                const res = await fetch(`https://menu.searchisend.in/apimenu/v2/v2_billing.php?action=get_order_details&id=${orderId}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const json = await res.json();
                if (json.success) setOrder(json.order);
            } catch (err) {
                console.error("Order fetch error:", err);
            } finally {
                setLoading(false);
            }
        };

        if (orderId) fetchOrderDetails();
    }, [orderId, token]);

    const handlePrint = () => {
        window.print();
    };

    if (loading) return <div style={{ textAlign: 'center', padding: '50px' }}>Loading Invoice...</div>;
    if (!order) return <div style={{ textAlign: 'center', padding: '50px' }}>Order not found.</div>;

    const taxableSubtotal = parseFloat(order.sub_total || 0) - parseFloat(order.discount_total || 0);
    const gstPercent = parseFloat(order.gst_percent || 0);
    const serviceChargePercent = parseFloat(order.service_charge || 0);
    const gstAmount = taxableSubtotal * (gstPercent / 100);
    const taxTotal = parseFloat(order.tax_total || 0);
    const serviceChargeAmount = taxTotal - gstAmount > 0 ? taxTotal - gstAmount : 0;
    const deliveryFee = parseFloat(order.delivery_fee || 0);

    // Styles for the print preview
    const containerStyle = {
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.7)',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '20px',
        overflowY: 'auto'
    };

    const controlsStyle = {
        backgroundColor: '#fff',
        padding: '12px 20px',
        borderRadius: '20px',
        marginBottom: '20px',
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: '10px',
        alignItems: 'center',
        boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
        width: '100%',
        maxWidth: '500px'
    };

    return createPortal(
        <div style={containerStyle} className="no-print-bg">
            <div style={controlsStyle} className="no-print">
                <span style={{ fontWeight: '600', marginRight: '10px' }}>Format:</span>
                <select value={layout} onChange={e => setLayout(e.target.value)} style={{ padding: '8px', border: '1px solid #ddd', borderRadius: '6px' }}>
                    <option value="58mm">58mm Thermal</option>
                    <option value="80mm">80mm Thermal</option>
                    <option value="A4">A4 Invoice</option>
                </select>
                <button onClick={handlePrint} style={{ padding: '8px 20px', background: 'var(--primary-orange)', color: '#fff', border: 'none', borderRadius: '50px', cursor: 'pointer', fontWeight: '700' }}>
                    <i className="fas fa-print" style={{ marginRight: '8px' }} />Print
                </button>
                <button onClick={onClose} style={{ padding: '8px 20px', background: '#f1f5f9', border: 'none', borderRadius: '50px', cursor: 'pointer', fontWeight: '600' }}>Close</button>
            </div>

            {/* --- ACTUAL INVOICE AREA --- */}
            <div className={`invoice-paper layout-${layout}`} style={{ backgroundColor: '#fff', padding: (layout === 'A4' ? '40px' : '20px'), width: '100%', maxWidth: (layout === '80mm' ? '302px' : layout === '58mm' ? '219px' : '800px'), minHeight: (layout === 'A4' ? '1100px' : 'auto'), boxShadow: '0 0 20px rgba(0,0,0,0.2)', position: 'relative' }}>
                
                {/* Header */}
                <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                    <h2 style={{ margin: '0 0 5px 0', fontSize: (layout === 'A4' ? '24px' : '18px') }}>{order.biz_name || "Invoice"}</h2>
                    <p style={{ margin: '0 0 3px 0', fontSize: '0.85em', whiteSpace: 'pre-line' }}>{order.biz_address || ""}</p>
                    {order.biz_phone && <p style={{ margin: 0, fontSize: '0.85em' }}><b>Ph:</b> {order.biz_phone}</p>}
                    {order.gstin && <p style={{ margin: '5px 0 0 0', fontSize: '0.85em' }}><b>GSTIN:</b> {order.gstin}</p>}
                </div>

                <div style={{ borderTop: '1px solid #000', borderBottom: '1px solid #000', padding: '10px 0', marginBottom: '15px', fontSize: '0.9em', display: 'flex', justifyContent: 'space-between' }}>
                    <div>
                        <p style={{ margin: '0 0 3px 0' }}><b>Order:</b> {order.order_no}</p>
                        <p style={{ margin: 0 }}><b>Date:</b> {new Date(order.created_at).toLocaleString()}</p>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                        <p style={{ margin: '0 0 3px 0' }}><b>Type:</b> {order.order_type || 'Dine-in'}</p>
                        {order.table_no && order.order_type === 'Dine-in' && <p style={{ margin: 0 }}><b>Table:</b> {order.table_no}</p>}
                        {order.order_type !== 'Dine-in' && <p style={{ margin: 0 }}><b>Status:</b> {order.order_status}</p>}
                    </div>
                </div>

                {/* Customer Details for Takeaway/Delivery */}
                {(order.customer_name || order.customer_phone) && (
                    <div style={{ marginBottom: '15px', fontSize: '0.9em', borderBottom: '1px dashed #000', paddingBottom: '10px' }}>
                        <p style={{ margin: '0 0 3px 0' }}><b>Customer:</b> {order.customer_name || 'Guest'}</p>
                        {order.customer_phone && <p style={{ margin: '0 0 3px 0' }}><b>Phone:</b> {order.customer_phone}</p>}
                        {order.order_type === 'Delivery' && order.customer_address && (
                            <p style={{ margin: 0 }}><b>Address:</b> {order.customer_address}</p>
                        )}
                    </div>
                )}

                {/* Items Table */}
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9em', marginBottom: '20px' }}>
                    <thead>
                        <tr style={{ borderBottom: '1px dashed #000' }}>
                            <th style={{ textAlign: 'left', paddingBottom: '5px' }}>Item</th>
                            <th style={{ textAlign: 'center', paddingBottom: '5px' }}>Qty</th>
                            <th style={{ textAlign: 'right', paddingBottom: '5px' }}>Rate</th>
                            <th style={{ textAlign: 'right', paddingBottom: '5px' }}>Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        {order.items.map((it, idx) => (
                            <tr key={idx}>
                                <td style={{ paddingTop: '8px' }}>{it.item_name}</td>
                                <td style={{ textAlign: 'center', paddingTop: '8px' }}>{it.quantity}</td>
                                <td style={{ textAlign: 'right', paddingTop: '8px' }}>{it.price}</td>
                                <td style={{ textAlign: 'right', paddingTop: '8px' }}>{it.total}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {/* Totals */}
                <div style={{ borderTop: '1px dashed #000', paddingTop: '10px', fontSize: '1em' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                        <span>Subtotal:</span>
                        <span>{order.sub_total}</span>
                    </div>
                    {gstAmount > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                            <span>GST ({gstPercent}%):</span>
                            <span>{gstAmount.toFixed(2)}</span>
                        </div>
                    )}
                    {serviceChargeAmount > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                            <span>Service Charge ({serviceChargePercent}%):</span>
                            <span>{serviceChargeAmount.toFixed(2)}</span>
                        </div>
                    )}
                    {deliveryFee > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                            <span>Delivery Fee:</span>
                            <span>{deliveryFee.toFixed(2)}</span>
                        </div>
                    )}
                    {parseFloat(order.discount_total) > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                            <span>Discount:</span>
                            <span>-{order.discount_total}</span>
                        </div>
                    )}
                    {parseFloat(order.loyalty_discount) > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                            <span>Loyalty (-{order.loyalty_points_redeemed} pts):</span>
                            <span>-{order.loyalty_discount}</span>
                        </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', fontSize: '1.2em', borderTop: '1px solid #000', paddingTop: '8px', marginTop: '5px' }}>
                        <span>GRAND TOTAL:</span>
                        <span>₹{order.grand_total}</span>
                    </div>
                </div>

                <div style={{ marginTop: '30px', textAlign: 'center', fontSize: '0.85em' }}>
                    <p style={{ margin: '0 0 5px 0', fontWeight: '600' }}>{order.footer_note || "Thank you for visiting us!"}</p>
                    <p style={{ margin: 0 }}>Generated by Smart Digital Menu</p>
                </div>

            </div>

            {/* Print Specific CSS */}
            <style>{`
                @media print {
                    @page { margin: 0; size: auto; }
                    #root { display: none !important; }
                    body { margin: 0; padding: 0; background: #fff !important; }
                    .no-print { display: none !important; }
                    .no-print-bg { 
                        position: absolute !important; 
                        inset: 0 !important; 
                        background: #fff !important; 
                        z-index: 1 !important; 
                        display: block !important;
                    }
                    .invoice-paper { 
                        position: absolute !important; 
                        left: 0 !important; 
                        top: 0 !important; 
                        width: ${layout === 'A4' ? '210mm' : layout === '80mm' ? '80mm' : '58mm'} !important;
                        box-shadow: none !important; 
                        margin: 0 !important; 
                        padding: 10px !important;
                        min-height: 0 !important;
                    }
                }
                .invoice-paper { font-family: 'Courier New', Courier, monospace; color: #000; }
                .layout-A4 { font-family: 'Inter', sans-serif !important; border: 1px solid #eee; }
            `}</style>
        </div>,
        document.body
    );
};

export default InvoicePreview;
